import React, { useEffect, useRef } from 'react';
import { MapPin, TrendingUp } from 'lucide-react';
import { projectsData } from '../data/mock';

export const Projects = () => {
  const projectRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry, index) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('project-visible');
            }, index * 100);
          }
        });
      },
      { threshold: 0.1 }
    );

    projectRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="projects" className="py-16 sm:py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 lg:mb-20">
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[#0A1F3D] mb-4 sm:mb-6">
            Featured <span className="text-[#C9A14A]">Projects</span>
          </h2>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4">
            Discover premium properties and investment opportunities across prime locations
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#C9A14A] to-transparent mx-auto mt-6 sm:mt-8" />
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {projectsData.map((project, index) => (
            <div
              key={project.id}
              ref={(el) => (projectRefs.current[index] = el)}
              className="project-card group relative bg-white rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-700"
            >
              {/* Image Container */}
              <div className="relative h-56 sm:h-64 md:h-72 lg:h-80 overflow-hidden">
                <img
                  src={project.image}
                  alt={project.name}
                  className="w-full h-full object-cover transform transition-transform duration-700 group-hover:scale-110"
                />
                {/* Enhanced Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F3D]/95 via-[#0A1F3D]/50 to-transparent" />
                
                {/* Premium Gold Accent */}
                <div className="absolute top-4 sm:top-6 right-4 sm:right-6 w-16 h-16 sm:w-20 sm:h-20 bg-[#C9A14A]/30 rounded-full blur-2xl" />
              </div>

              {/* Content */}
              <div className="p-5 sm:p-6 md:p-8 relative">
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#0A1F3D] mb-2 sm:mb-3 group-hover:text-[#C9A14A] transition-colors duration-300">
                  {project.name}
                </h3>
                
                <div className="flex items-center text-gray-600 mb-3 sm:mb-4">
                  <MapPin className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-[#C9A14A] flex-shrink-0" />
                  <span className="text-sm sm:text-base">{project.location}</span>
                </div>

                <p className="text-sm sm:text-base text-gray-700 mb-4 sm:mb-6 line-clamp-2 leading-relaxed">
                  {project.description}
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-2">
                  {project.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="text-xs px-4 py-2 bg-[#C9A14A]/10 text-[#C9A14A] rounded-full font-semibold hover:bg-[#C9A14A] hover:text-[#0A1F3D] transition-all duration-300"
                    >
                      {feature}
                    </span>
                  ))}
                </div>

                {/* Hover Indicator */}
                <div className="absolute bottom-8 right-8 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <TrendingUp className="w-7 h-7 text-[#C9A14A]" />
                </div>
              </div>

              {/* Enhanced Border Glow Effect */}
              <div className="absolute inset-0 rounded-3xl border-2 border-transparent group-hover:border-[#C9A14A]/60 transition-all duration-500 pointer-events-none" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
